import { computed, unref } from 'vue';
import { provideLocal, injectLocal } from '@vueuse/core';
import { useType as useOrientation } from '@modules/types';
import { useTraits } from '@composables/useTraits';
import useUrlTemplate from '@composables/useUrlTemplate';
import { useConsolidatedLatchkey } from '@modules/latchkey/composables';
import { useProductType } from '@composables/useProductType';
import { useProductFlags } from '@modules/featureControl';
import { useUpc } from '@modules/upc';
import { useProduct } from '@modules/product';

const IMAGE_CAROUSEL_REPOSITORY = Symbol.for('IMAGE_CAROUSEL_REPOSITORY');
/**
 * @param {string} videoId
 * @param {{ filePath: string; name: string; showJumboSwatch: boolean; swatchSpriteOffset: number; swatchSpriteUrlIndex: number }} firstImage
 */
const imagesAdapter = (videoId, firstImage) => ({
  videoId,
  filePath: firstImage?.filePath,
  name: firstImage?.name || '',
  video: true,
  showJumboSwatch: firstImage?.showJumboSwatch,
  swatchSpriteOffset: firstImage?.swatchSpriteOffset || 0,
  swatchSpriteUrlIndex: firstImage?.swatchSpriteUrlIndex || 0,
});

/**
 * @param {Object} options
 * @param {string} options.urlTemplate
 * @param {import('vue').ComputedRef<Image[]>} options.images
 * @param {string} options.videoId
 */
export function _useImages({
  urlTemplate,
  images, videoId,
}) {
  const selectedColorImages = computed(() => {
    if (videoId) {
      return [
        ...images.value.slice(0, 3),
        imagesAdapter(videoId, images.value[0]),
        ...images.value.slice(3),
      ];
    }
    return images.value;
  });

  const primaryImagePath = computed(() => (selectedColorImages.value[0]?.filePath ? urlTemplate + selectedColorImages.value[0]?.filePath : ''));
  const primaryImageData = computed(() => {
    const product = selectedColorImages.value[0] || '';
    return {
      ...product,
      path: primaryImagePath.value,
    };
  });
  const carouselImages = computed(() => selectedColorImages.value.map((image) => ({
    ...image,
    path: urlTemplate + image.filePath,
  })));

  return {
    carouselImages,
    primaryImageData,
  };
}

/**
 * @param {Image[]} images
 */
function swapFirst2Images(images) {
  if (images.length < 2) {
    return images;
  }
  const [img1, img2, ...restOfImages] = images;
  return [img2, img1, ...restOfImages];
}

/**
 *
 * @param {import('@vueuse/core').MaybeRefOrGetter<number | null>} selectedOrientationId
 * @returns {boolean}
 */
function isRightArmOrientation(selectedOrientationId) {
  return unref(selectedOrientationId) === 1;
}

/**
 * @param {string} productId
 * @param {Image[]} images
 * @returns {Image[]}
 */
function setImageOrderByOrientation(productId, images) {
  const { isBigTicketItemFlag } = useProductFlags();
  const { selectedTypeId } = useOrientation();

  if (isBigTicketItemFlag && isRightArmOrientation(selectedTypeId)) {
    return swapFirst2Images(images);
  }
  return images;
}

/**
 *
 * @param {string} productId
 * @param {Product} product
 * @returns {import('vue').ComputedRef<Image[]>}
 */
function useRawImages(productId, product) {
  const { relationships } = product;
  const { isConsolidatedLatchKey, isConsolidatedStandard, isConsolidatedMaster } = useProductType();

  if (isConsolidatedLatchKey) {
    return useConsolidatedLatchkey().images;
  }

  const { selectedColor } = useTraits();

  if (isConsolidatedStandard || isConsolidatedMaster) {
    const { selectedUpcId } = useUpc();

    return computed(
      () => (selectedUpcId.value && relationships?.upcs?.[unref(selectedUpcId)]?.imagery?.images)
        || selectedColor.value?.imagery.images
        || product?.imagery?.images
        || [],
    );
  }

  return computed(() => {
    const images = selectedColor.value?.imagery?.images || product?.imagery.images || [];
    return setImageOrderByOrientation(productId, images);
  });
}

export function useImagesProvide() {
  const { productId, productData } = useProduct();

  const { productUrlTemplate } = useUrlTemplate();

  const imagesRaw = useRawImages(productId, productData);
  const images = computed(() => imagesRaw.value.filter(Boolean)); // TODO: Drop null images on server side
  const videoId = productData?.imagery.videoId || '';

  const {
    primaryImageData, carouselImages,
  } = _useImages({
    urlTemplate: productUrlTemplate, images, videoId,
  });

  const videoIndex = computed(() => carouselImages.value.findIndex(((image) => image.video)));

  const toProvide = {
    primaryImageData,
    carouselImages,
    videoId,
    videoIndex,
  };
  provideLocal(IMAGE_CAROUSEL_REPOSITORY, toProvide);
  return toProvide;
}

/**
 * @returns {ReturnType<typeof useImagesProvide>}
 */
export const useImages = () => injectLocal(IMAGE_CAROUSEL_REPOSITORY);

export default useImages;
